import React from "react";
import Section from "./Section";
import SectionHeading from "./SectionHeading";
import { routingCandidates } from "./heroFixtures";

/**
 * Backed by lambda/shared/routing-engine.mjs's findBestReviewer(), live in
 * production since backlog item 13 (file-expertise backfill + forward
 * accumulation on merge) and item 16 (self-declared expertise tags removed —
 * this is the whole formula now, not one input among several).
 */
const ANNOTATIONS = [
  {
    label: "File expertise, not job titles",
    body: "Scored from how many times a reviewer has actually modified the exact files in a PR — automatically maintained, never self-reported.",
  },
  {
    // Scoped down 2026-08-27. This previously claimed routing "works from day
    // one, not after weeks of waiting for data to accumulate" — an overclaim.
    // backfill-expertise's resolveUserId (lines 145-156) only credits commits
    // whose author ALREADY has a users row on that team; everyone else lands in
    // skippedUnknownUser and is discarded. On a fresh install that's usually the
    // installing admin alone. Don't restore the stronger wording without first
    // fixing the backfill (tracked in CLAUDE.md's Post-MVP Priorities).
    label: "Backfilled from real history",
    body: "Built from up to 6 months of your repo's real commit history the moment it connects — so routing has signal from the first PR, for every teammate who's signed up.",
  },
  {
    label: "Balanced against workload",
    body: "Expertise never means one person gets every review — how many reviews someone already has pending is weighed in the same score. On Free, routing balances workload alone; history-aware matching is part of Pro.",
  },
  {
    // Added with the review write-back. Worth its own line rather than a
    // clause elsewhere: "we pick the right reviewer" is only useful if that
    // person finds out, and until this shipped they didn't unless they opened
    // our dashboard.
    label: "The reviewer actually hears about it",
    body: "Anthurium requests them on the pull request itself, so they get GitHub's own notification and it lands in their review-requested queue — no new tool to check.",
  },
  {
    label: "Kept current automatically",
    body: "Every merge updates the record. Nothing to fill out, nothing to keep up to date, nothing that goes stale the moment someone changes teams.",
  },
];

function CandidateRow({ candidate }) {
  const initials = candidate.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  return (
    <div
      className={`flex items-center gap-3.5 rounded-lg px-4 py-3.5 ${
        candidate.winner ? "bg-rf-accent/10 border border-rf-accent/30" : "bg-rf-bg-tertiary"
      }`}
    >
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
        style={{
          background: candidate.winner ? "rgba(0,212,170,0.15)" : "rgba(139,149,171,0.12)",
          color: candidate.winner ? "#00d4aa" : "#8b95ab",
        }}
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-rf-text-primary">
          {candidate.name}
        </div>
        <div className="font-mono text-[11px] text-rf-text-secondary">
          {candidate.winner
            ? `Assigned — file expertise score: ${candidate.fileExpertise}, queue depth: ${candidate.queueDepth}`
            : `file expertise: ${candidate.fileExpertise} · queue depth: ${candidate.queueDepth}`}
        </div>
      </div>
      {candidate.winner && (
        <span className="shrink-0 rounded-full bg-rf-accent/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-rf-accent">
          Assigned
        </span>
      )}
    </div>
  );
}

export default function RoutingSpotlight() {
  return (
    <Section id="routing" variant="elevated">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div className="order-2 mx-auto w-full max-w-md lg:order-1">
          <div
            aria-hidden="true"
            className="rounded-2xl border border-rf-border bg-rf-bg-secondary p-4 shadow-2xl"
          >
            <div className="mb-3 px-1 font-mono text-[11px] text-rf-text-muted">
              Reviewers considered for lambda/shared/auth.mjs
            </div>
            <div className="flex flex-col gap-2.5">
              {routingCandidates.map((c) => (
                <CandidateRow key={c.username} candidate={c} />
              ))}
            </div>
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <SectionHeading
            eyebrow="Reviewer routing"
            title="Not whoever's next in line — whoever's touched this code"
            align="left"
          />
          <dl className="mt-8 flex flex-col gap-6">
            {ANNOTATIONS.map((item) => (
              <div key={item.label}>
                <dt className="mb-1 text-sm font-semibold text-rf-text-primary">
                  {item.label}
                </dt>
                <dd className="text-sm leading-relaxed text-rf-text-secondary">{item.body}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </Section>
  );
}
